import { makeAutoObservable, reaction } from 'mobx'
import { apiService } from '@/services/Api'
import {
  ApplicationListItem,
  ApplicationListMeta,
  ApplicationStatus,
  GetApplicationListRequest,
} from '@/services/Api/generated'
import { Loader } from '@/helpers/Loader'

export enum ApplicationFilter {
  FILTER_STATUS_ISSUED = 'FILTER_STATUS_ISSUED',
  FILTER_STATUS_SEND_INTO_ROSPATENT = 'FILTER_STATUS_SEND_INTO_ROSPATENT',
  FILTER_STATUS_EXPERTISE = 'FILTER_STATUS_EXPERTISE',
  FILTER_STATUS_PAYMENT = 'FILTER_STATUS_PAYMENT',
  // FILTER_STATUS_ON_COMPLETED: 'FILTER_STATUS_ON_COMPLETED',
}

export const ApplicationTabs = {
  ALL: 'ALL',
  ...ApplicationFilter,
}
type ApplicationTabsKeys = keyof typeof ApplicationTabs
export type ApplicationTabs = (typeof ApplicationTabs)[ApplicationTabsKeys]

export function isApplicationTabs(value: string): value is ApplicationTabs {
  return value in ApplicationTabs
}

const tabsToStatusMap: { [key in ApplicationTabs]: ApplicationStatus } = {
  [ApplicationTabs.ALL]: ApplicationStatus.All,
  [ApplicationTabs.FILTER_STATUS_EXPERTISE]: ApplicationStatus.StatusExpertise,
  [ApplicationTabs.FILTER_STATUS_SEND_INTO_ROSPATENT]:
    ApplicationStatus.StatusSendIntoRospatent,
  [ApplicationTabs.FILTER_STATUS_PAYMENT]: ApplicationStatus.StatusPayment,
  [ApplicationTabs.FILTER_STATUS_ISSUED]: ApplicationStatus.StatusIssued,
}

export class ApplicationsStore {
  perPage: number = 9
  page: number = 1
  total: number = 0
  search = ''
  tab: ApplicationTabs = ApplicationTabs.ALL
  list: ApplicationListItem[] = []
  meta: ApplicationListMeta = {
    FILTER_ALL: 0,
    FILTER_STATUS_EXPERTISE: 0,
    FILTER_STATUS_PAYMENT: 0,
    FILTER_STATUS_ISSUED: 0,
    FILTER_STATUS_SEND_INTO_ROSPATENT: 0,
  }

  loaderData = new Loader(
    async () => {
      this.setList([])

      let params: GetApplicationListRequest = {
        page: this.page,
        perPage: this.perPage,
      }

      if (this.tab !== ApplicationTabs.ALL) {
        params.status = tabsToStatusMap[this.tab]
      }

      if (this.search.length !== 0) {
        params.search = this.search
      }

      let response = await apiService.application.getApplicationList(params)

      if (response.status) {
        if (this.page > response.pagination.lastPage) {
          this.setPage(response.pagination.lastPage)
          params.page = response.pagination.lastPage
          response = await apiService.application.getApplicationList(params)
        }
        this.setTotal(response.pagination.totalRecords)
        this.setMeta(response.meta)
        this.setList(response.data)
      }
    },
    { withDebounce: true }
  )

  get pages() {
    return Math.ceil(this.total / this.perPage)
  }

  get tabs() {
    return [
      {
        value: ApplicationTabs.ALL,
        label: 'Все статусы',
        badge: this.meta['FILTER_ALL'] ?? 0,
      },
      {
        value: ApplicationTabs.FILTER_STATUS_SEND_INTO_ROSPATENT,
        label: 'Подготовка к подаче',
        badge:
          this.meta[ApplicationTabs.FILTER_STATUS_SEND_INTO_ROSPATENT] ?? 0,
      },
      {
        value: ApplicationTabs.FILTER_STATUS_PAYMENT,
        label: 'Ожидает оплаты',
        badge: this.meta[ApplicationTabs.FILTER_STATUS_PAYMENT] ?? 0,
      },
      {
        value: ApplicationTabs.FILTER_STATUS_EXPERTISE,
        label: 'Экспертиза',
        badge: this.meta[ApplicationTabs.FILTER_STATUS_EXPERTISE] ?? 0,
      },
      {
        value: ApplicationTabs.FILTER_STATUS_ISSUED,
        label: 'Завершенные заявки',
        badge: this.meta[ApplicationTabs.FILTER_STATUS_ISSUED] ?? 0,
        isButton: true,
      },
    ]
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  resetData() {
    this.perPage = 9
    this.page = 1
    this.total = 0
    this.search = ''
    this.tab = ApplicationStatus.All
    this.list = []
    this.meta = {
      FILTER_ALL: 0,
      FILTER_STATUS_EXPERTISE: 0,
      FILTER_STATUS_PAYMENT: 0,
      FILTER_STATUS_ISSUED: 0,
      FILTER_STATUS_SEND_INTO_ROSPATENT: 0,
    }
  }

  setPage(page: number) {
    this.page = page
  }
  setPerPage(perPage: number) {
    this.perPage = perPage
  }
  setTotal(total: number) {
    this.total = total
  }
  setTab(value: ApplicationTabs) {
    this.tab = value
  }
  setSearch(value: string) {
    this.search = value
  }
  setList(list: ApplicationListItem[]) {
    this.list = list
  }
  setMeta(meta: ApplicationListMeta) {
    this.meta = meta
  }
}
